import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit2, X, Save, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import BulkActionsBar from "@/components/bulk/BulkActionsBar";
import ChangeHistoryPanel from "@/components/bulk/ChangeHistoryPanel";
import PageHeader from "@/components/PageHeader";

export default function HolidaysPage() {
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // holiday obj or {} for new
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyId, setHistoryId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: h }, { data: z }] = await Promise.all([
        api.get("/holidays", { params: { year, page_size: 500 } }),
        api.get("/zones", { params: { page_size: 50 } }),
      ]);
      setItems(h.items || []);
      setZones(z.items || []);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const zoneMap = useMemo(() => {
    const m = {}; zones.forEach((z) => { m[z.id] = z; }); return m;
  }, [zones]);

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.name}"?`)) return;
    try {
      await api.delete(`/holidays/${h.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-holidays-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Holidays" }]}
        title="Holiday calendar"
        subtitle="Zone-specific national + regional holidays. Empty zones = applies to all."
        favorite={{ key: "admin.holidays", type: "screen", label: "Holiday calendar", route: "/admin/holidays" }}
        testid="holidayspage-page-header"
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Holiday calendar</h1>
          <p className="text-sm text-neutral-500">Zone-specific national + regional holidays. Empty zones = applies to all.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="number" min={2020} max={2100} value={year}
                 onChange={(e) => setYear(Number(e.target.value))}
                 className="w-24" data-testid="holidays-year" />
          <BulkActionsBar masterKey="holidays" label="Holidays" onImportDone={() => load()} />
          <Button onClick={() => setEditing({ zone_ids: [], date: "", name: "", description: "" })}
                  className="tulsi-gradient text-white" data-testid="holidays-new">
            <Plus className="h-4 w-4 mr-2" /> Add holiday
          </Button>
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="holidays-table">
            <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-500 tracking-widest">
              <tr>
                <th className="text-left py-3 px-4">Date</th>
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">Description</th>
                <th className="text-left py-3 px-4">Zones</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="py-10 text-center text-neutral-400">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={5} className="py-10 text-center text-neutral-400">
                  <CalendarDays className="h-6 w-6 mx-auto mb-2 opacity-50" /> No holidays for {year}
                </td></tr>
              )}
              {items.map((h) => (
                <tr key={h.id} className="border-t border-neutral-100" data-testid={`holidays-row-${h.date}`}>
                  <td className="py-3 px-4 font-mono">{h.date}</td>
                  <td className="py-3 px-4 font-medium">{h.name}</td>
                  <td className="py-3 px-4 text-neutral-500">{h.description || "—"}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      {(h.zone_ids || []).length === 0 ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#FFD93D]/40 text-neutral-700">All zones</span>
                      ) : (h.zone_ids || []).map((zid) => (
                        <span key={zid} className="text-[11px] px-2 py-0.5 rounded-full bg-[#6D2C91]/10 text-[#6D2C91]">
                          {zoneMap[zid]?.code || zid.slice(0, 4)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => { setHistoryId(h.id); setHistoryOpen(true); }}
                            data-testid={`holidays-history-${h.date}`}>
                      History
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(h)}
                            data-testid={`holidays-edit-${h.date}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(h)}
                            className="text-red-600" data-testid={`holidays-delete-${h.date}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editing && (
        <HolidayForm holiday={editing} zones={zones} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      <ChangeHistoryPanel open={historyOpen} onOpenChange={setHistoryOpen}
                          entityType="holidays" entityId={historyId} title="Holiday history" />
    </div>
  );
}

function HolidayForm({ holiday, zones, onClose, onSaved }) {
  const [form, setForm] = useState(holiday);
  const [busy, setBusy] = useState(false);
  const isNew = !form.id;

  const toggleZone = (zid) => {
    const cur = new Set(form.zone_ids || []);
    cur.has(zid) ? cur.delete(zid) : cur.add(zid);
    setForm((s) => ({ ...s, zone_ids: [...cur] }));
  };

  const save = async () => {
    if (!form.date || !form.name) return toast.error("Date and name required");
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/holidays", form);
        toast.success("Holiday added");
      } else {
        await api.patch(`/holidays/${form.id}`, form);
        toast.success("Holiday updated");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="holiday-form-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add holiday" : "Edit holiday"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date || ""}
                     onChange={(e) => setForm({ ...form, date: e.target.value })}
                     data-testid="holiday-form-date" />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="holiday-form-name" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                   data-testid="holiday-form-desc" />
          </div>
          <div>
            <Label>Applies to zones (empty = all)</Label>
            <div className="flex flex-wrap gap-2 pt-2">
              {zones.map((z) => {
                const selected = (form.zone_ids || []).includes(z.id);
                return (
                  <button key={z.id} type="button" onClick={() => toggleZone(z.id)}
                          data-testid={`holiday-form-zone-${z.code}`}
                          className={`text-xs px-3 py-1.5 rounded-full border transition ${
                            selected ? "bg-[#6D2C91] text-white border-[#6D2C91]" :
                            "bg-white text-neutral-700 border-neutral-200 hover:border-[#6D2C91]"
                          }`}>
                    {z.code} · {z.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="tulsi-gradient text-white" data-testid="holiday-form-save">
            <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
